<template>
  <div
    class="tgmag-timepicker"
    ref="timepickerEl"
    :class="{
      'tgmag-timepicker--active': this.active
    }"
  >
    <div
      v-if="timeFormatted"
      class="tgmag-timepicker__input form-control"
      @keydown.enter="show('hours', ...arguments)"
      @keydown.space="show('hours', ...arguments)"
      @click="click('hours', ...arguments)"
    >
      <p
        ref="hoursInput"
        tabindex="0"
        @keydown.enter="show('hours', ...arguments)"
        @keydown.space="show('hours', ...arguments)"
        @click="show('hours', ...arguments)"
        class="tgmag-timepicker__picker"
        :class="[
          {
            'tgmag-timepicker__picker--focused': this.focus === 'hours'
          },
          {
            'tgmag-timepicker__picker--placeholder': this.values.hours === null
          }
        ]"
      >
        <template v-if="values.hours">
          {{ values.hours }}
        </template>
        <template v-else>
          HH
        </template>
      </p>
      <p>:</p>
      <p
        ref="minutesInput"
        tabindex="0"
        @keydown.enter="show('minutes', ...arguments)"
        @keydown.space="show('minutes', ...arguments)"
        @click="show('minutes', ...arguments)"
        class="tgmag-timepicker__picker"
        :class="[
          {
            'tgmag-timepicker__picker--focused': this.focus === 'minutes'
          },
          {
            'tgmag-timepicker__picker--placeholder':
              this.values.minutes === null
          }
        ]"
      >
        <template v-if="values.minutes">
          {{ values.minutes }}
        </template>
        <template v-else>
          MM
        </template>
      </p>
    </div>
    <div
      v-show="active"
      ref="timepickerContainer"
      class="tgmag-timepicker__dropdown"
    >
      <div
        class="tgmag-timepicker__group tgmag-timepicker__group--hours"
        :class="{
          'tgmag-timepicker__group--focused': this.focus === 'hours'
        }"
      >
        <i
          class="icon-up"
          tabindex="0"
          :class="{ disabled: hoursScroll === 0 }"
          @click="clickToScroll('up', 'hoursScroll')"
        ></i>
        <div
          ref="hoursScroll"
          @scroll="scrollTracker('hoursScroll', ...arguments)"
          class="tgmag-timepicker__group-shell"
        >
          <span
            v-for="(hour, index) in defaultHours"
            tabindex="0"
            :aria-label="hour + ' hour'"
            :key="hour.id"
            :class="[
              { selected: hour === values.hours },
              { disabled: isDisabled(hour) }
            ]"
            @click="selection(hour, 'hours', index, ...arguments)"
            @keydown.enter="selection(hour, 'hours', index, ...arguments)"
            @keydown.space="selection(hour, 'hours', index, ...arguments)"
          >
            {{ hour }}
          </span>
        </div>
        <i
          class="icon-down"
          tabindex="0"
          :class="{ disabled: hoursScroll === hoursScrollMax }"
          @click="clickToScroll('down', 'hoursScroll')"
        ></i>
      </div>
      <div
        class="tgmag-timepicker__group tgmag-timepicker__group--minutes"
        :class="{
          'tgmag-timepicker__group--focused': this.focus === 'minutes'
        }"
      >
        <i
          class="icon-up"
          tabindex="0"
          :class="{ disabled: minutesScroll === 0 }"
          @click="clickToScroll('up', 'minutesScroll')"
        ></i>
        <div
          ref="minutesScroll"
          @scroll="scrollTracker('minutesScroll', ...arguments)"
          class="tgmag-timepicker__group-shell"
        >
          <span
            v-for="(minutes, index) in defaultMinutes"
            tabindex="0"
            :aria-label="minutes + ' minutes'"
            :key="minutes.id"
            :class="{ selected: minutes === values.minutes }"
            @click="selection(minutes, 'minutes', index, ...arguments)"
            @keydown.enter="selection(minutes, 'minutes', index, ...arguments)"
            @keydown.space="selection(minutes, 'minutes', index, ...arguments)"
          >
            {{ minutes }}
          </span>
        </div>
        <i
          class="icon-down"
          tabindex="0"
          :class="{ disabled: minutesScroll === minutesScrollMax }"
          @click="clickToScroll('down', 'minutesScroll')"
        ></i>
      </div>
    </div>
  </div>
</template>

<script>
import smoothscroll from "smoothscroll-polyfill";

smoothscroll.polyfill();

export default {
  name: "TimePicker",
  props: {
    defaultHours: {
      type: Array,
      default: function() {
        return [
          "00",
          "01",
          "02",
          "03",
          "04",
          "05",
          "06",
          "07",
          "08",
          "09",
          "10",
          "11",
          "12",
          "13",
          "14",
          "15",
          "16",
          "17",
          "18",
          "19",
          "20",
          "21",
          "22",
          "23"
        ];
      }
    },
    defaultMinutes: {
      type: Array,
      default: function() {
        return [
          "00",
          "05",
          "10",
          "15",
          "20",
          "25",
          "30",
          "35",
          "40",
          "45",
          "50",
          "55"
        ];
      }
    },
    label: {
      type: String,
      default: "Label"
    },
    disabledOptions: {
      type: Array,
      default: function() {
        return [];
      }
    },
    openDropdown: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      values: {
        hours: null,
        minutes: null,
        hoursIndex: 0,
        minutesIndex: 0
      },
      active: false,
      focus: null,
      hoursScroll: 0,
      hoursScrollMax: 0,
      minutesScroll: 0,
      minutesScrollMax: 0
    };
  },
  computed: {
    timeFormatted() {
      return this.values.hours + ":" + this.values.minutes;
    }
  },
  watch: {
    "values.hours": {
      immediate: true,
      handler() {
        this.focus = "minutes";
      }
    },
    "values.minutes": {
      immediate: true,
      handler() {
        this.active = false;
      }
    },
    timeFormatted: {
      immediate: true,
      handler(value) {
        this.$emit("input", value);
      }
    },
    openDropdown: {
      immediate: true,
      handler(value) {
        this.active = value;
        this.focus = "hours";
      }
    },
    active: {
      immediate: true,
      handler(value, oldValue) {
        if (oldValue != undefined && value === false) {
          /*
           ** Fire Blur when user has interacted atleast once and active state is false
           */
          this.$emit("blur");
        }
      }
    }
  },
  methods: {
    selection(value, part, index) {
      const indexKey = part + "Index";
      this.values[part] = value;
      this.values[indexKey] = index;
      this.focus = part === "hours" ? "minutes" : null;
    },
    click(part, event) {
      if (
        event.target != this.$refs.hoursInput &&
        event.target != this.$refs.minutesInput
      ) {
        if (this.active) {
          this.close();
        } else {
          this.show(part);
        }
      }
    },
    show(part) {
      if (this.focus != part) {
        this.active = true;
        this.$emit("focus");
      } else {
        this.active = !this.active;
      }
      this.focus = part;
    },
    isDisabled(option) {
      return this.disabledOptions.includes(option);
    },
    scrollTracker(type, event) {
      this[type] = event.target.scrollTop;
    },
    clickToScroll(direction, type) {
      const target = this.$refs[type];
      if (direction === "up") {
        target.scrollTo({
          top: this[type] - 128,
          behavior: "smooth"
        });
      } else if (direction === "down") {
        target.scrollTo({
          top: this[type] + 128,
          behavior: "smooth"
        });
      }
    },
    close() {
      this.active = false;
    }
  },
  mounted() {
    const self = this;
    const timepickerEl = this.$refs.timepickerEl;
    const hScroll = this.$refs.hoursScroll;
    const mScroll = self.$refs.minutesScroll;
    document.addEventListener("click", function(event) {
      var isClickInside = timepickerEl.contains(event.target);
      if (!isClickInside) {
        self.active = false;
      }
    });
    const observer = new MutationObserver(function() {
      self.hoursScrollMax = hScroll.scrollHeight - hScroll.clientHeight;
      self.minutesScrollMax = mScroll.scrollHeight - mScroll.clientHeight;
    });
    observer.observe(this.$refs.timepickerContainer, { attributes: true });
  },
  created() {
    window.addEventListener("blur", this.close);
  },
  destroyed() {
    window.removeEventListener("blur", this.close);
  }
};
</script>

<style lang="scss">
@import "../../assets/scss/variables.scss";
$dropdown-max-width: 120px;
.tgmag-timepicker {
  position: relative;
  // width: $dropdown-max-width;
  width: 100%;
  display: block;
  &__input {
    // border: 1px solid $input-button-inactive;
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    background-color: white;
    // width: $dropdown-max-width;
    // padding: 4px 0;
    > p {
      padding: 8px 2px;
      margin: 0;
      line-height: 1em;
      font-size: 14px;
      font-weight: theme-weight("regular");
    }
  }
  &__picker {
    text-align: center;
    border-radius: 4px;
    &:hover,
    &:focus {
      background: $input-button-inactive;
    }
    &--placeholder {
      color: theme-color("accent");
    }
    .tgmag-timepicker--active & {
      &--focused {
        background: $input-button-inactive;
      }
    }
  }
  &__dropdown {
    position: absolute;
    z-index: 999;
    top: 54px;
    left: 0;
    max-height: 260px;
    overflow: hidden;
    display: flex;
    width: $dropdown-max-width;
    border: 1px solid $secondary;
    background-color: white;
    border-top: 12px solid $success;
  }
  &__group {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: $dropdown-max-width / 2;
    padding: 4px 2px;

    [class^="icon-"],
    [class*=" icon-"] {
      cursor: pointer;
      width: 100%;
      text-align: center;
      position: relative;
      &.disabled {
        opacity: 0.2;
      }
    }
    .icon-up {
      border-bottom: 1px solid $input-button-inactive;
      padding-bottom: 5px;
    }
    .icon-down {
      padding-top: 5px;
      border-top: 1px solid $input-button-inactive;
    }

    &:before {
      pointer-events: none;
      content: "";
      height: 15px;
      width: 100%;
      position: absolute;
      top: 24px;
      left: 0;
      background: -moz-linear-gradient(
        top,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        top,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 0) 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#ffffff', endColorstr='#00ffffff',GradientType=0 ); /* IE6-9 */
    }
    &:after {
      pointer-events: none;
      content: "";
      height: 15px;
      width: 100%;
      position: absolute;
      bottom: 24px;
      left: 0;
      background: -moz-linear-gradient(
        top,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        top,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#00ffffff', endColorstr='#ffffff',GradientType=0 ); /* IE6-9 */
    }
    &:first-child {
      border-right: 1px solid $input-button-inactive;
    }
    span {
      width: 44px;
      padding: 4px 0;
      margin: 0 0 1px 0;
      text-align: center;
      border-radius: 4px;
      cursor: pointer;
      &:hover,
      &:focus {
        color: white;
        background: theme-color("success");
      }
      &.selected {
        color: white;
        background: theme-color("success");
      }
      &.disabled {
        display: none;
        pointer-events: none;
        opacity: 0.2;
        background: transparent !important;
      }
    }
    &:not(.tgmag-timepicker__group--focused) {
      opacity: 0.5;
      span.selected {
        color: theme-color("primary");
        background: transparent;
      }
    }
  }
  &__group-shell {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-height: 260px;
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
